import React from 'react';
import { Helmet } from 'react-helmet-async';
import { ShieldCheck, Check } from 'lucide-react';

const Screening = () => {
  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Forebyggende Screening for Kræft | Hellerup Lægehus</title>
        <meta name="description" content="Få information om screeningsprogrammer for livmoderhalskræft, brystkræft og tarmkræft hos Hellerup Lægehus. Se hvornår du indkaldes, og hvordan det foregår." />
        <meta property="og:title" content="Forebyggende Screening for Kræft | Hellerup Lægehus" />
        <meta property="og:description" content="Få information om screeningsprogrammer for livmoderhalskræft, brystkræft og tarmkræft hos Hellerup Lægehus. Se hvornår du indkaldes, og hvordan det foregår." />
        <html lang="da-DK" />
      </Helmet>

      {/* Hero Section */}
      <section className="bg-slate-50 pt-20 pb-12 md:pt-24 md:pb-16">
        <div className="max-w-7xl mx-auto px-6">
          <h1 className="text-4xl md:text-5xl font-sans font-medium text-black mb-6 leading-tight tracking-tight">
            Screening
          </h1>
          <p className="text-lg text-slate-600 max-w-3xl leading-relaxed">
            For at undgå kræft og andre alvorlige sygdomme tilbydes forskellige screeningsprogrammer i Danmark. 
            Herunder kan du finde information om de forskellige tilbud.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            <div className="lg:col-span-2 space-y-12">
              <div>
                <p className="text-slate-700 leading-relaxed mb-8">
                  Du får automatisk tilbudt de relevante screeningsundersøgelsen via e-boks, hvis du tilhører den relevante aldersgrupper og køn for screeningsprogrammet. Du skal således ikke selv henvende dig for at blive indkaldt til undersøgelse. En screening er et frivilligt tilbud, som du til enhver tid kan framelde dig.
                </p>
                
                <h2 className="text-2xl font-sans font-medium text-black mb-6 tracking-tight">
                  Her kan du læse mere om screening for:
                </h2>

                <div className="space-y-6">
                  <div className="flex gap-4 items-start">
                    <div className="mt-1">
                      <Check className="w-5 h-5 text-black" />
                    </div>
                    <div>
                      <h3 className="text-lg font-sans font-medium text-black mb-1">Livmoderhalskræft</h3>
                      <p className="text-slate-600">
                        Kvinder 23-64 år, frem til 49 år hvert 3. år, derefter hvert 5. år. 
                        <span className="block mt-2 font-medium text-black">Denne undersøgelse foregår hos os.</span>
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-4 items-start">
                    <div className="mt-1">
                      <Check className="w-5 h-5 text-black" />
                    </div>
                    <div>
                      <h3 className="text-lg font-sans font-medium text-black mb-1">Brystkræft</h3>
                      <p className="text-slate-600">
                        Kvinder 50-69 år, hvert andet år.
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-4 items-start">
                    <div className="mt-1">
                      <Check className="w-5 h-5 text-black" />
                    </div>
                    <div>
                      <h3 className="text-lg font-sans font-medium text-black mb-1">Tarmkræft</h3>
                      <p className="text-slate-600">
                        Mænd og kvinder 50-74 år, hvert andet år.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Sidebar/Info Box */}
            <div className="lg:col-span-1">
              <div className="bg-[#4b717a] text-white p-8 space-y-6">
                <div className="flex items-center gap-3 mb-2">
                  <ShieldCheck className="w-6 h-6 text-white/90" />
                  <h3 className="text-xl font-sans font-medium">Vigtig information</h3>
                </div>
                <p className="text-white/90 text-sm leading-relaxed">
                  Husk at tjekke din e-boks løbende for indkaldelser. Hvis du har symptomer eller er bekymret, bør du altid kontakte lægen, uanset om du er i et screeningsforløb eller ej.
                </p>
                <div className="pt-4 border-t border-white/20">
                  <p className="text-xs text-white/70 italic">
                    Screening er et forebyggende tiltag for personer uden symptomer.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Screening;
